import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { firebaseConfig } from 'src/config';

const app = initializeApp(firebaseConfig);

// App Check attests that requests come from our genuine, untampered app. Once
// enforcement is enabled for the Authentication / Firestore products in the
// Firebase console, Google rejects any request without a valid token — blocking
// scripted signup / password-reset / abuse from outside the app.
//
// NOTE: App Check covers EVERY Firebase Auth call, including Google sign-in
// (signInWithPopup), not just email/password — so this client wiring must be
// live (deployed) BEFORE enforcement is turned on, or all sign-in breaks.
// Initialized as early as possible, before getAuth/getFirestore, per Firebase
// guidance.
//
// Guarded on the env var so this is a safe no-op until the reCAPTCHA v3 SITE
// key is configured for the project (e.g. local dev before keys exist). Use the
// PUBLIC site key here (it ships in the JS bundle); the private SECRET key goes
// ONLY in the Firebase Console App Check registration, never in client code.
const appCheckSiteKey = import.meta.env.VITE_FIREBASE_APPCHECK_KEY;
if (appCheckSiteKey) {
  // Local dev only: lets localhost obtain App Check tokens without reCAPTCHA.
  // Gated on the localhost app URL — Vite inlines VITE_FE_APP_URL as a string
  // literal at build time, so for any deployed build this comparison folds to
  // `false` and the whole branch (token and all) is dead-code eliminated.
  //
  // We pin the token via VITE_FIREBASE_APPCHECK_DEBUG_TOKEN (any UUID you
  // choose; kept in the gitignored .env) so it stays stable across reloads,
  // browsers and machines — register that same value once in Firebase Console →
  // App Check → Apps → (web app) → "Manage debug tokens". If the var is unset
  // we fall back to `true`, which makes Firebase mint a random token and log it
  // to the browser console for you to register instead.
  if (import.meta.env.VITE_FE_APP_URL === 'http://localhost:3000') {
    (self as unknown as Record<string, unknown>).FIREBASE_APPCHECK_DEBUG_TOKEN =
      import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN || true;
  }
  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(appCheckSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

export const auth = getAuth(app);
export const db = getFirestore(app);
export default app;
